import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Clock } from "lucide-react";
import type { RelatedArticle } from "@/lib/related-articles";

interface Props {
  articles: RelatedArticle[];
  /** Sector short name, used so headings stay specific. */
  sector: string;
}

export function RelatedArticlesModule({ articles, sector }: Props) {
  if (articles.length === 0) return null;

  return (
    <section className="border-t border-border bg-card/40 px-6 py-20 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">
          Related reading
        </p>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-brand sm:text-4xl">
          Related HR help articles for {sector} employers
        </h2>
        <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
          Matched to this sector by the topics and questions {sector.toLowerCase()} employers bring
          to us most often, rather than by publication date.
        </p>

        <ul className="mt-12 grid gap-6 sm:grid-cols-2">
          {articles.map(({ post, intentLabel, reason, matchedTerms }) => (
            <li key={post.slug}>
              <Link
                to="/hr-insights/$slug"
                params={{ slug: post.slug }}
                className="group flex h-full flex-col rounded-md border border-border bg-card p-6 transition-colors hover:border-accent/60 hover:bg-secondary"
              >
                <span className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.18em] text-accent">
                  {intentLabel}
                </span>
                <span className="mt-3 font-serif text-lg leading-snug text-brand group-hover:text-accent">
                  {post.title}
                </span>
                <span className="mt-3 text-sm leading-relaxed text-muted-foreground">{reason}</span>

                {matchedTerms.length > 0 ? (
                  <span className="mt-4 flex flex-wrap gap-2">
                    {matchedTerms.map((term) => (
                      <span
                        key={term}
                        className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground"
                      >
                        {term}
                      </span>
                    ))}
                  </span>
                ) : null}

                <span className="mt-6 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="size-3.5" aria-hidden="true" />
                    {post.readingTime} min read · {post.category}
                  </span>
                  <ArrowUpRight
                    className="size-4 text-accent transition-transform group-hover:-translate-y-0.5"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
